import { IconName } from '@wui/common-library'

import { PLATFORM } from './types'

export type UserAgentView = {
  icon: IconName
  textKey: string
}

const USER_AGENT_VIEWS: (UserAgentView & { match: string })[] = [
  { match: PLATFORM.ASTRA_LINUX, icon: 'astraLinux', textKey: 'chat.astraLinuxDescription' },
  { match: PLATFORM.NODE_JS, icon: 'nodeJS', textKey: 'chat.nodeJSDescription' },
  { match: PLATFORM.LINUX, icon: 'linux', textKey: 'chat.linuxDescription' },
  { match: PLATFORM.MACOS, icon: 'macos', textKey: 'chat.macOSDescription' },
  { match: PLATFORM.IOS, icon: 'macos', textKey: 'chat.iOSDescription' },
  { match: PLATFORM.ANDROID, icon: 'android', textKey: 'chat.androidDescription' },
  { match: PLATFORM.WINDOWS, icon: 'win', textKey: 'chat.windowsDescription' },
  { match: PLATFORM.BOT, icon: 'bot', textKey: 'chat.botDescription' },
]

const WEB_VIEW: UserAgentView = {
  icon: 'web',
  textKey: 'chat.webDescription',
}

export const hasRecognizedPlatform = (userAgent: string): boolean => {
  const normalized = userAgent.toLocaleLowerCase()
  return USER_AGENT_VIEWS.some(({ match }) => normalized === match || normalized.includes(match))
}

/** SIP shells that intentionally mean web (browser / installed PWA), not native desktop. */
export const isExplicitWebShellUserAgent = (userAgent: string): boolean => {
  const normalized = userAgent.toLocaleLowerCase()
  return normalized.includes('webclientrtu-browser') || normalized.includes('webclientrtu-app')
}

const isNativePresenceAgent = (agent: string): boolean =>
  hasRecognizedPlatform(agent) && agent.toLocaleLowerCase() !== PLATFORM.WEB

/**
 * Conference call.userAgent is SIP UA (may lack OS). Presence userAgents are IM platforms.
 * Prefer call UA when it encodes a platform or an explicit web shell; otherwise fall back to presence.
 */
export const resolveConferenceUserAgent = (
  callUserAgent: string,
  presenceUserAgents?: Array<string | null> | null,
): string => {
  if (callUserAgent && (hasRecognizedPlatform(callUserAgent) || isExplicitWebShellUserAgent(callUserAgent))) {
    return callUserAgent
  }

  const presence = [...new Set((presenceUserAgents ?? []).filter((agent): agent is string => Boolean(agent)))]
  const recognized = presence.filter(hasRecognizedPlatform)

  if (recognized.length === 1) {
    return recognized[0]!
  }

  if (recognized.length > 1) {
    const native = recognized.find(isNativePresenceAgent)
    return native ?? recognized[0]!
  }

  if (presence[0]) {
    return presence[0]
  }

  return callUserAgent
}

export const getUserAgentView = (userAgent?: string | null): UserAgentView | null => {
  if (!userAgent) {
    return null
  }

  const normalized = userAgent.toLocaleLowerCase()
  const view = USER_AGENT_VIEWS.find(({ match }) => normalized === match || normalized.includes(match))

  return view
    ? { icon: view.icon, textKey: view.textKey }
    : WEB_VIEW
}
